import axios from "axios";
import { API_BASE_URL } from "../../../../../js/api.js";
import { IAcompanhamentoStrategy } from "./IAcompanhamentoStrategy.js";
import {
  resolverEmpresaDoNivelEmpresa,
  listarAcompanhamentosNivelEmpresa,
  listarAcompanhamentosGenericos,
} from "../empresaAcompanhamentoService.js";
import { carregarDadosEmpresaPorId } from "../../../../services/empresaService.js";
import {
  obterTipoProgramaUsuarioLogado,
  obterTipoProgramaExplicitoUsuario,
} from "../../utils/tipoPrograma.js";
import {
  normalizarEmpresaAcompanhamentoItem,
  deduplicarAcompanhamentosPorEmpresa,
  obterTipoProgramaItem,
} from "../../mappers/empresaMapper.js";
import {
  extrairIdItem,
  itemCorrespondeAoTermo,
  itemPertenceAoPrograma,
  resolverCnpjItem,
} from "../../utils/itemResolvers.js";
import { criarLogger, extrairListaDeResposta } from "../acompanhamentoHelpers.js";

const logEmpresa = criarLogger("Strategy:Empresa");

/**
 * Estratégia especializada para o perfil Empresa.
 *
 * Princípios SOLID:
 * - SRP (Single Responsibility): Responsável exclusivo por identificar a empresa
 *   logada, consultar seus contratos no programa ativo (TCE 0, TCA 1, TCA Empregador 2)
 *   e garantir isolamento total de dados entre estabelecimentos.
 * - OCP (Open/Closed): Permite estender comportamentos de busca e mapeamento por programa
 *   sem impactar a página de Acompanhamento ou os outros perfis.
 * - LSP (Liskov Substitution): Implementa IAcompanhamentoStrategy retornando lista normalizada.
 * - DIP (Dependency Inversion): Consome abstrações e utilitários compartilhados.
 */
export class EmpresaAcompanhamentoStrategy extends IAcompanhamentoStrategy {
  async carregar({ token, contexto = {}, identidade = {}, nivel, signal } = {}) {
    logEmpresa("Carregando acompanhamentos para perfil Empresa...");

    // 1. Identificar ID e Programa da empresa ativa
    const tipoProgExplicito = obterTipoProgramaExplicitoUsuario();
    const tipoProg = tipoProgExplicito ?? obterTipoProgramaUsuarioLogado();
    let empId =
      contexto.empresaId ||
      identidade?.empresa_id ||
      identidade?.id_empresa ||
      identidade?.usuarioId ||
      identidade?.id ||
      null;

    if (!empId && typeof window !== "undefined") {
      try {
        const rawUser = window.localStorage.getItem("user");
        if (rawUser) {
          const u = JSON.parse(rawUser);
          empId = u.empresa_id || u.id_empresa || (u.tipo === "empresa" ? u.id : null);
        }
      } catch (_) {}
    }

    // 2. Resolver dados consolidados da empresa (razão social, CNPJ, formulários)
    let unica = null;
    try {
      unica = await resolverEmpresaDoNivelEmpresa(token);
    } catch (errResolv) {
      logEmpresa("Aviso ao resolver empresa inicial:", errResolv?.message);
    }

    if (!unica?.id && empId) {
      unica = {
        id: empId,
        ...(tipoProgExplicito !== null ? { tipo_programa: tipoProgExplicito } : {}),
      };
    }

    // Se identificamos o ID da empresa (ex.: 529801) mas falta razão social ou CNPJ, enriquece via carregarDadosEmpresaPorId
    if (unica?.id && (!unica.cnpj || !unica.razao_social)) {
      try {
        const dadosCompletos = await carregarDadosEmpresaPorId(unica.id, { signal });
        if (dadosCompletos) {
          unica = {
            ...dadosCompletos,
            ...unica,
            id: unica.id || dadosCompletos.id,
            cnpj: unica.cnpj || dadosCompletos.cnpj || dadosCompletos.documento || "",
            razao_social:
              unica.razao_social ||
              dadosCompletos.razao_social ||
              dadosCompletos.nome_empresa ||
              "",
          };
        }
      } catch (errEnrich) {
        logEmpresa("Aviso ao enriquecer dados da empresa:", errEnrich?.message);
      }
    }

    const idFinal = unica?.id || empId;
    logEmpresa("Empresa identificada para acompanhamento:", {
      id: idFinal,
      tipoProg,
      tipoProgExplicito,
      cnpj: unica?.cnpj,
      razao_social: unica?.razao_social,
    });

    // 3. Busca direcionada aos endpoints do programa da empresa via serviço especializado
    let itensEmpresa = [];
    let buscaNivelEmpresaConcluida = false;
    let ultimoErro = null;
    if (idFinal) {
      const empresaParaBusca = {
        ...(unica || {}),
        id: idFinal,
        ...(tipoProgExplicito !== null ? { tipo_programa: tipoProgExplicito } : {}),
      };

      try {
        itensEmpresa = await listarAcompanhamentosNivelEmpresa({
          token,
          unica: empresaParaBusca,
          contexto,
          log: logEmpresa,
        });
        buscaNivelEmpresaConcluida = true;
      } catch (errListar) {
        ultimoErro = errListar;
        logEmpresa("Falha na listagem nível empresa, acionando busca direta:", errListar?.message);
      }
    }

    // 4. Busca direta de resiliência caso a listagem acima não tenha concluído ou não havia idFinal
    if ((!Array.isArray(itensEmpresa) || itensEmpresa.length === 0) && !buscaNivelEmpresaConcluida) {
      logEmpresa("Buscando autorizações diretamente nos endpoints setoriais da empresa...");
      itensEmpresa = await this._buscarDiretoNosEndpoints({
        token,
        empresaId: idFinal,
        unica,
        tipoProg,
        tipoProgExplicito,
        contexto,
        signal,
      });
    }

    // 5. Suporte a busca direta por ID do contrato (ex.: "109", "#109")
    const termoIdLimpo = String(contexto?.busca || "").replace(/\D/g, "");
    if (termoIdLimpo) {
      const jaContem = itensEmpresa.some((it) => String(extrairIdItem(it)) === termoIdLimpo);
      if (!jaContem) {
        const itemPorId = await this._buscarContratoPorId({
          token,
          id: termoIdLimpo,
          unica,
          tipoProg,
          tipoProgExplicito,
          signal,
        });
        if (itemPorId) {
          itensEmpresa.unshift(itemPorId);
        }
      }
    }

    // 6. Fallback final via listagem genérica se a busca nível empresa falhou
    if (itensEmpresa.length === 0 && !buscaNivelEmpresaConcluida) {
      logEmpresa("Acionando fallback de acompanhamentos genéricos...");
      try {
        const listaFallback = await listarAcompanhamentosGenericos({
          token,
          nivel,
          identidade,
          busca: contexto.busca,
          empresaId: idFinal,
        });
        if (Array.isArray(listaFallback) && listaFallback.length > 0) {
          itensEmpresa = listaFallback;
        }
      } catch (errFallback) {
        ultimoErro = ultimoErro || errFallback;
        logEmpresa("Fallback genérico também falhou:", errFallback?.message);
      }
    }

    if (itensEmpresa.length === 0 && ultimoErro) {
      throw ultimoErro;
    }

    // 7. Normalização robusta e isolamento dos itens da empresa
    const cnpjUnica = String(unica?.cnpj || unica?.documento || "").replace(/\D/g, "");
    const itensNormalizados = itensEmpresa
      .map((it) => {
        const tpItem =
          obterTipoProgramaItem(it) ??
          (it.tipo_programa !== undefined && it.tipo_programa !== null
            ? Number(it.tipo_programa)
            : tipoProg);
        const origemItem = String(
          it._origem ||
            (tpItem === 0 ? "tce" : tpItem === 2 ? "tca-empregador" : "tca"),
        )
          .toLowerCase()
          .replace(/_/g, "-");
        const etapasItem =
          it.total_etapas || (tpItem === 1 ? 4 : 5);

        const itemNorm = normalizarEmpresaAcompanhamentoItem({
          ...(unica || {}),
          ...it,
          empresa_id: it.empresa_id || idFinal,
          tipo_programa: tpItem,
          _origem: origemItem,
          total_etapas: etapasItem,
        });
        return itemNorm;
      })
      .filter(Boolean)
      .filter((it) => {
        if (tipoProgExplicito !== null && !itemPertenceAoPrograma(it, tipoProgExplicito)) {
          return false;
        }
        const cnpjItem = resolverCnpjItem(it).replace(/\D/g, "");
        if (
          cnpjUnica.length === 14 &&
          cnpjItem.length === 14 &&
          cnpjItem.slice(0, 8) !== cnpjUnica.slice(0, 8)
        ) {
          return false;
        }
        return true;
      });

    let listaDeduplicada = deduplicarAcompanhamentosPorEmpresa(itensNormalizados);

    // 8. Aplica busca textual se presente
    if (contexto.busca && !termoIdLimpo) {
      listaDeduplicada = listaDeduplicada.filter((it) =>
        itemCorrespondeAoTermo(it, contexto.busca),
      );
    }

    logEmpresa("Acompanhamentos retornados para empresa:", listaDeduplicada.length);
    return listaDeduplicada;
  }

  /**
   * Consulta diretamente os endpoints específicos do programa da empresa com isolamento de falhas.
   */
  async _buscarDiretoNosEndpoints({ token, empresaId, unica, tipoProg, contexto, signal }) {
    const itens = [];
    const headers = { Authorization: `Bearer ${token}` };

    if (tipoProg === 0) {
      // 1. Programa TCE (Estágio - Empresa 529801)
      const endpointsTce = [
        `${API_BASE_URL}/acompanhamentos/me`,
        empresaId ? `${API_BASE_URL}/tce/empresa/${empresaId}/autorizacao` : null,
        empresaId ? `${API_BASE_URL}/tce?empresa_id=${empresaId}` : null,
        `${API_BASE_URL}/tce/estudantes-empresa`,
        unica?.cnpj ? `${API_BASE_URL}/tce?cnpj=${encodeURIComponent(unica.cnpj)}` : null,
        `${API_BASE_URL}/tce`,
      ].filter(Boolean);

      const resultados = await Promise.allSettled(
        endpointsTce.map(async (url) => {
          const resp = await axios.get(url, {
            params: {
              ...(empresaId && !url.includes("?") ? { empresa_id: empresaId } : {}),
              ...(contexto?.busca ? { busca: contexto.busca } : {}),
              tipo_programa: 0,
              origem: "tce",
            },
            headers,
            timeout: 10000,
            signal,
          });
          const lista = extrairListaDeResposta(resp?.data);
          return lista.map((it) => {
            const contractId = it.id ?? it.tce_id ?? it.autorizacao_id;
            return {
              ...(unica || {}),
              ...it,
              id: contractId ?? it.id ?? empresaId,
              tce_id: contractId ?? it.id,
              empresa_id: it.empresa_id || empresaId,
              tipo_programa: 0,
              _origem: "tce",
              total_etapas: 5,
              raw: {
                ...(unica || {}),
                ...it,
                id: contractId ?? it.id ?? empresaId,
                tce_id: contractId ?? it.id,
                empresa_id: it.empresa_id || empresaId,
                tipo_programa: 0,
                _origem: "tce",
                total_etapas: 5,
              },
            };
          });
        }),
      );

      for (const r of resultados) {
        if (r.status === "fulfilled" && Array.isArray(r.value)) {
          for (const item of r.value) {
            if (!empresaId || String(item.empresa_id) === String(empresaId) || !item.empresa_id) {
              itens.push(item);
            }
          }
        }
      }
    } else if (tipoProg === 2) {
      // 2. Programa TCA Empregador
      const endpointsTcaEmp = [
        empresaId ? `${API_BASE_URL}/tca-empregador?empresa_id=${empresaId}` : null,
        empresaId ? `${API_BASE_URL}/tca-empregador/empresa/${empresaId}/autorizacao` : null,
        empresaId ? `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao` : null,
        `${API_BASE_URL}/tca-empregador`,
        `${API_BASE_URL}/acompanhamentos/me`,
      ].filter(Boolean);

      const resultados = await Promise.allSettled(
        endpointsTcaEmp.map(async (url) => {
          const resp = await axios.get(url, {
            params: {
              ...(empresaId && !url.includes("?") ? { empresa_id: empresaId } : {}),
              ...(contexto?.busca ? { busca: contexto.busca } : {}),
              tipo_programa: 2,
              origem: "tca-empregador",
            },
            headers,
            timeout: 10000,
            signal,
          });
          const lista = extrairListaDeResposta(resp?.data);
          return lista.map((it) => {
            const contractId =
              it.id ?? it.tca_id ?? it.tca_empregador_id ?? it.autorizacao_id;
            return {
              ...(unica || {}),
              ...it,
              id: contractId ?? it.id ?? empresaId,
              tca_id: contractId ?? it.id,
              tca_empregador_id: contractId ?? it.id,
              empresa_id: it.empresa_id || empresaId,
              tipo_programa: 2,
              _origem: "tca-empregador",
              total_etapas: 5,
              raw: {
                ...(unica || {}),
                ...it,
                id: contractId ?? it.id ?? empresaId,
                tca_id: contractId ?? it.id,
                tca_empregador_id: contractId ?? it.id,
                empresa_id: it.empresa_id || empresaId,
                tipo_programa: 2,
                _origem: "tca-empregador",
                total_etapas: 5,
              },
            };
          });
        }),
      );

      for (const r of resultados) {
        if (r.status === "fulfilled" && Array.isArray(r.value)) {
          for (const item of r.value) {
            if (!empresaId || String(item.empresa_id) === String(empresaId) || !item.empresa_id) {
              itens.push(item);
            }
          }
        }
      }
    } else {
      // 3. Programa TCA Comum
      const endpointsTca = [
        empresaId ? `${API_BASE_URL}/tca/empresa/${empresaId}/autorizacao` : null,
        empresaId ? `${API_BASE_URL}/tca?empresa_id=${empresaId}` : null,
        `${API_BASE_URL}/tca`,
        `${API_BASE_URL}/acompanhamentos/me`,
      ].filter(Boolean);

      const resultados = await Promise.allSettled(
        endpointsTca.map(async (url) => {
          const resp = await axios.get(url, {
            params: {
              ...(empresaId && !url.includes("?") ? { empresa_id: empresaId } : {}),
              ...(contexto?.busca ? { busca: contexto.busca } : {}),
              tipo_programa: 1,
              origem: "tca",
            },
            headers,
            timeout: 10000,
            signal,
          });
          const lista = extrairListaDeResposta(resp?.data);
          return lista.map((it) => {
            const contractId = it.id ?? it.tca_id ?? it.autorizacao_id;
            return {
              ...(unica || {}),
              ...it,
              id: contractId ?? it.id ?? empresaId,
              tca_id: contractId ?? it.id,
              empresa_id: it.empresa_id || empresaId,
              tipo_programa: 1,
              _origem: "tca",
              total_etapas: 4,
              raw: {
                ...(unica || {}),
                ...it,
                id: contractId ?? it.id ?? empresaId,
                tca_id: contractId ?? it.id,
                empresa_id: it.empresa_id || empresaId,
                tipo_programa: 1,
                _origem: "tca",
                total_etapas: 4,
              },
            };
          });
        }),
      );

      for (const r of resultados) {
        if (r.status === "fulfilled" && Array.isArray(r.value)) {
          for (const item of r.value) {
            if (!empresaId || String(item.empresa_id) === String(empresaId) || !item.empresa_id) {
              itens.push(item);
            }
          }
        }
      }
    }

    return itens;
  }

  /**
   * Busca direta de contrato por ID numérico (#109 ou 109).
   */
  async _buscarContratoPorId({ token, id, unica, tipoProg, tipoProgExplicito = null, signal }) {
    const rotas = [
      { url: `${API_BASE_URL}/tce/${id}`, tp: 0, origem: "tce", etapas: 5 },
      { url: `${API_BASE_URL}/tca/${id}`, tp: 1, origem: "tca", etapas: 4 },
      { url: `${API_BASE_URL}/tca-empregador/${id}`, tp: 2, origem: "tca-empregador", etapas: 5 },
    ].filter((r) => tipoProgExplicito === null || r.tp === tipoProgExplicito);
    for (const rota of rotas) {
      try {
        const resp = await axios.get(rota.url, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
          signal,
        });
        const dados = resp?.data?.data || resp?.data;
        if (dados && (dados.id || dados.tce_id || dados.tca_id || dados.autorizacao_id)) {
          return {
            ...(unica || {}),
            ...dados,
            id: dados.id || id,
            tipo_programa: dados.tipo_programa ?? rota.tp,
            _origem: dados._origem || rota.origem,
            total_etapas: rota.etapas,
            raw: {
              ...(unica || {}),
              ...dados,
              tipo_programa: dados.tipo_programa ?? rota.tp,
              _origem: dados._origem || rota.origem,
              total_etapas: rota.etapas,
            },
          };
        }
      } catch (_) {}
    }
    return null;
  }
}
